import { useState } from "react";
import { toast } from "sonner";
import { Plus, Check, X } from "@phosphor-icons/react";
import { apiErrorMessage } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export function CategorySelect({ client, categories, value, onChange, onCategoryAdded }) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [coarse] = useState(() => window.matchMedia?.("(pointer: coarse)").matches || false);

  const add = async () => {
    if (!name.trim()) return;
    try {
      const data = await client.addCategory(name);
      onCategoryAdded?.(data);
      onChange(data.id);
      toast.success(data.existing ? `“${data.name}” already exists — selected it` : `Category “${data.name}” added`);
      setAdding(false); setName("");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not add category"));
    }
  };

  if (adding) {
    return (
      <div className="flex items-center gap-1.5">
        <Input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } if (e.key === "Escape") setAdding(false); }}
          placeholder="New category, e.g. Masonry"
          data-testid="record-new-category-input"
        />
        <button type="button" onClick={add} className="p-2 rounded-md bg-[#327de3] text-white hover:bg-[#1f64c2]" data-testid="record-new-category-save"><Check size={14} weight="bold" /></button>
        <button type="button" onClick={() => setAdding(false)} className="p-2 rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50" data-testid="record-new-category-cancel"><X size={14} /></button>
      </div>
    );
  }

  const addBtn = (
    <button
      type="button"
      onClick={() => setAdding(true)}
      className="inline-flex items-center gap-1 px-2.5 h-9 rounded-md border border-dashed border-slate-300 text-xs font-semibold text-slate-600 hover:border-[#327de3] hover:text-[#1f64c2] whitespace-nowrap transition-colors"
      data-testid="record-add-category-btn"
    >
      <Plus size={12} weight="bold" /> Add Category
    </button>
  );

  // Touch devices get the native picker: reliable inside scrolling modals on phones.
  if (coarse) {
    return (
      <div className="flex items-center gap-1.5">
        <select
          value={value || ""}
          onChange={(e) => e.target.value && onChange(e.target.value)}
          className="flex-1 h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-800"
          data-testid="record-category-select"
        >
          <option value="" disabled>Choose category</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {addBtn}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger className="flex-1" data-testid="record-category-select">
          <SelectValue placeholder="Choose category" />
        </SelectTrigger>
        <SelectContent>
          {categories.map((c) => (
            <SelectItem key={c.id} value={c.id} data-testid={`record-category-option-${c.name_key}`}>{c.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {addBtn}
    </div>
  );
}
